import {
  Button,
  color,
  Confirm,
  css,
  getFontWeight,
  Heading,
  Lighter2,
  LinkUnbrokenIcon,
  OnyxGray,
  Paragraph,
  spaceToPx,
  styled,
} from 'canoe';
import { compose, connectWithHammock, ENDPOINTS, useActions } from 'hammock';
import React, { useState } from 'react';
import { CopyToClipboard as Clipboard } from 'react-copy-to-clipboard';
import { useLocation } from 'react-router';
import { APP_URL } from 'site-constants';
import { FlexRow } from 'styleguide';

import { ContentType } from '@candu-ai/content/types';
import { DocumentPreview } from '@candu-ai/document/preview';
import { useEventing } from 'commons/eventing';
import { useAuthentication, useNavigation, useWorkspacePath } from 'commons/hooks';
import { getCategoryName } from 'commons/templates';
import { refresh } from 'commons/window';
import { useActiveTooltip } from 'craft/components/Tooltip';
import { BuiltInComponents, CRAFT_ROOT_ID } from 'craft/constants';
import { useEditor, useMutations, useSelection } from 'craft/hooks';
import { CONTENT_PATH } from 'dashboard/pages/Content/constants';
import { CREATE_ROUTE } from 'dashboard/pages/Content/pages/Create/constants';

import TemplateCardById from './TemplateCardById';
import {
  CategoryPill,
  CreatorCard,
  CreatorDetails,
  Header,
  HeaderSubtitle,
  HeaderTitle,
  StyledColumns,
  StyledContent,
  StyledImageBackground,
  Tag,
  TagsContainer,
} from './internal';
import { TemplateMetadata } from './types';

const Transform = styled.div`
  height: 660px;
  left: 0;
  position: absolute;
  width: 1240px;
  top: 0;
  transform: scale(0.38) translate(-77.5%, -77%);
`;

const TemplateContainer = styled.div`
  ${({ theme }) => css`
    background-color: ${theme.colors.GlitterGrayL20};
    border-radius: ${theme.borderRadius.regular};
    position: relative;
    z-index: 1;
    height: 310px;
    width: 510px;

    > div {
      padding: 1em;
      background-color: ${theme.colors.White};
    }
  `}
`;

interface Props {
  toggle: () => void;
  onClickBack: () => void;
  onSelectTemplate: () => void;
  documentId: number;
  template?: TemplateMetadata;
  templates?: TemplateMetadata[];
  templateDocument?: any;
}

const TemplatePreview = ({
  documentId,
  template,
  templates = [],
  templateDocument,
  toggle,
  onSelectTemplate,
}: Props) => {
  const eventing = useEventing();
  const navigate = useNavigation();
  const { pathname } = useLocation();
  const { query } = useEditor();
  const workspacePath = useWorkspacePath();
  const activeHotspot = useActiveTooltip();
  const [hasCopied, setHasCopied] = useState(false);
  const [showReplaceConfirm, setConfirmReplace] = useState(false);

  const { update } = useActions(ENDPOINTS.documentsEditor);

  const isDocumentModal = query.node(CRAFT_ROOT_ID)?.get()?.data?.name === BuiltInComponents.Modal;
  const { applicationId }: any = useAuthentication();
  const isOnExperiment = pathname.includes('experiments') && pathname.includes('variants');

  const relatedTemplates =
    template?.relatedTemplateIds ||
    templates
      .filter((el: TemplateMetadata) => el.category === template?.category && el.id !== template.id)
      .map((el: TemplateMetadata) => el.id);

  const mutations = useMutations();
  const { selection } = useSelection();

  const isTemplateModal =
    templateDocument?.document.nodes[templateDocument?.document.rootNodeId].type ===
    ContentType.Modal;

  const isTemplateTooltip =
    templateDocument?.document.nodes[templateDocument?.document.rootNodeId].type ===
      ContentType.HotspotGroup ||
    templateDocument?.document.nodes[templateDocument?.document.rootNodeId].type ===
      ContentType.Tour;

  const appendToTree = () => {
    eventing.track('template.insertedFromEditor', {
      templateId: template?.id,
      templateName: template?.name,
    });

    if (selection.isEmpty) {
      // insert as the last child of the first child, if there's no component selected.
      mutations.insertContentDocument(
        templateDocument.document,
        activeHotspot?.id || CRAFT_ROOT_ID,
      );
      toggle();
      return;
    }

    mutations.insertContentDocument(templateDocument.document, selection.last);
    toggle();
  };

  const replaceContent = () => {
    update({
      id: documentId,
      body: { document: templateDocument.document, id: documentId },
    }).then(() => refresh());
  };

  return (
    <div css={{ paddingRight: `${spaceToPx('l')}px` }}>
      <Header>
        <FlexRow>
          <div>
            <HeaderTitle
              element="h3"
              size="XS"
              strong
              css={{
                color: `${color(OnyxGray)}`,
              }}
            >
              {template?.name}
            </HeaderTitle>
            {template?.description && (
              <HeaderSubtitle size="short" css={{ fontSize: '14px' }}>
                {template?.description}
              </HeaderSubtitle>
            )}
          </div>
          {template?.category && (
            <CategoryPill variant="primary">{getCategoryName(template?.category)}</CategoryPill>
          )}
        </FlexRow>
        <FlexRow>
          <Clipboard
            text={`${APP_URL}${workspacePath(
              `${CONTENT_PATH}/${CREATE_ROUTE.path}?templateId=${template?.id}`,
            )}`}
          >
            <Button appearance="outline" variant="secondary" onClick={() => setHasCopied(true)}>
              <LinkUnbrokenIcon color="OnyxGrayD10" size="s" />
              &nbsp;&nbsp; {hasCopied ? 'Link Copied' : 'Copy link to share'}
            </Button>
          </Clipboard>
          {/* Make sure we have the document to allow the user to append it. */}
          {!isTemplateModal && !isTemplateTooltip && Boolean(templateDocument) && (
            <Button variant="primary" onClick={appendToTree}>
              Use this template
            </Button>
          )}
          {isTemplateModal && isDocumentModal && (
            <Button variant="primary" onClick={() => setConfirmReplace(true)}>
              Replace Content
            </Button>
          )}
          {(isTemplateModal || isTemplateTooltip) && !isOnExperiment && template && (
            <Button
              variant="primary"
              onClick={() => {
                toggle();
                navigate(`/content/new?templateId=${template.id}`);
              }}
            >
              Create new content
            </Button>
          )}
        </FlexRow>
      </Header>
      <FlexRow>
        <StyledContent>
          <StyledColumns>
            {template?.image && (
              <StyledImageBackground style={{ backgroundImage: `url(${template.image})` }} />
            )}
            {template && !template.image && (
              <TemplateContainer>
                <Transform>
                  <DocumentPreview
                    applicationId={applicationId}
                    documentId={template.id}
                    isTemplate
                  />
                </Transform>
              </TemplateContainer>
            )}
            <div>
              {template?.creatorName && (
                <CreatorCard>
                  <Paragraph size="short" css={{ fontWeight: getFontWeight('bold'), margin: 0 }}>
                    Created by
                  </Paragraph>
                  <CreatorDetails>
                    {template.creatorPicture && (
                      <img
                        alt="author"
                        src={template.creatorPicture}
                        css={{
                          width: '40px',
                          borderRadius: '50%',
                          marginRight: `${spaceToPx('s')}px`,
                        }}
                      />
                    )}
                    <div>
                      <Paragraph size="short" css={{ color: color(OnyxGray, Lighter2), margin: 0 }}>
                        {template.creatorName}
                      </Paragraph>
                      {template.creatorOrganizationName && (
                        <Paragraph css={{ margin: 0 }}>
                          <span css={{ color: color(OnyxGray, Lighter2) }}>at</span>
                          {template.creatorOrganizationPicture && (
                            <img
                              alt="author's organization"
                              src={template.creatorOrganizationPicture}
                              css={{
                                height: '20px',
                                marginLeft: '3px',
                                verticalAlign: 'sub',
                              }}
                            />
                          )}
                          &nbsp;{template.creatorOrganizationName}
                        </Paragraph>
                      )}
                    </div>
                  </CreatorDetails>
                </CreatorCard>
              )}
              {template?.tags && (
                <TagsContainer>
                  {template?.tags.map((tag) => (
                    <Tag variant="neutral">{tag}</Tag>
                  ))}
                </TagsContainer>
              )}
            </div>
          </StyledColumns>
          {template?.longDescription && (
            <div css={{ marginTop: `${spaceToPx('normal')}px` }}>
              <Heading element="h4" size="XS">
                About this template
              </Heading>
              <Paragraph size="long" css={{ color: color(OnyxGray, Lighter2) }}>
                {template.longDescription}
              </Paragraph>
            </div>
          )}
          <div css={{ marginTop: `${spaceToPx('normal')}px` }}>
            <Heading element="h4" size="XS">
              Related templates
            </Heading>
            <FlexRow css={{ marginTop: `${spaceToPx('s')}px` }}>
              {relatedTemplates.map(
                (templateId: number, index: number) =>
                  index < 2 && (
                    <TemplateCardById
                      key={templateId}
                      templateId={templateId}
                      onSelectTemplate={onSelectTemplate}
                    />
                  ),
              )}
            </FlexRow>
          </div>
        </StyledContent>
      </FlexRow>
      {showReplaceConfirm && (
        <Confirm
          severity="Danger"
          title="Replace Content"
          message="If you replace the content, all the changes you did will be lost."
          confirmButton={{
            'data-cy': 'confirm-replace',
            children: 'Replace',
          }}
          onClose={() => setConfirmReplace(false)}
          onConfirm={replaceContent}
        />
      )}
    </div>
  );
};

const withTemplateMetadata = connectWithHammock(({ templateId }) => ({
  resourceName: 'template',
  endpoint: ENDPOINTS.templateMetadata,
  id: templateId,
}));

const withEditorDocument = connectWithHammock(({ templateId }) => ({
  resourceName: 'templateDocument',
  endpoint: ENDPOINTS.templateDocumentsEditor,
  id: templateId,
  renderWhileLoading: true,
}));

const withTemplates = connectWithHammock(() => ({
  resourceName: 'templates',
  endpoint: ENDPOINTS.templateMetadata,
}));

export default compose(withTemplateMetadata, withEditorDocument, withTemplates)(TemplatePreview);
